import { useEffect, useRef } from 'react';
import { Region } from 'wavesurfer.js/src/plugin/regions';
const SRC = 'use-storyboard-ws.ts';

import { StoryBoardTracks } from 'services/storyboard.service';
import { RootState } from 'store/store';
import { useAppSelector, useAppDispatch, useAppStore } from 'store/store-hooks';
import { useAnimation } from 'components/_hooks/use-animation';
import { selectClipData, selectSbClipPlaybackParams } from 'components/app/_helpers/_app.helper';
import { EnumDragger } from 'components/app/_mediator/app.slice';
import { useDraggerContext } from 'components/app/_mediator/app-dragger.provider';
import { setSelectedFile } from 'components/app/projects/_mediator/projects.slice';
import { isRegionIn } from 'components/app/audio-track/_mediator/audio-track.helper';
import { setSbPosition } from 'components/app/editor/_mediator/editor-controls.slice';
import { ClipsMap, EMPTY_CELL, setActiveClip } from 'components/app/editor/_mediator/editor.slice';
import { isDraggingCell, getCursorCell } from 'components/app/editor/_mediator/editor-markers/editor-markers.helper';
import { setDampingPlayerIndex, setPlayback } from 'components/app/storyboard/sb-player/_mediator/sb-player.slice';
import { onInitEnum, useSbPlayerContext } from 'components/app/storyboard/sb-player/_mediator/sb-player.provider';
import { useSbPlayerTransition } from 'components/app/storyboard/sb-player/_mediator/use-sb-player-transition';
import { useSbPlayerPlayback } from 'components/app/storyboard/sb-player/_mediator/use-sb-player-playback';
import { useSbIdle } from 'components/app/storyboard/sb-player/_mediator/use-sb-player-idle';
import { useSbFader } from 'components/app/storyboard/sb-player/_mediator/use-sb-player-fader';
import { useSbPlayerCb } from 'components/app/storyboard/sb-player/_mediator/use-sb-player-cb';
import {
  getPlaybackPlayers,
  getPlayerTime,
  isLinkedGroup,
} from 'components/app/storyboard/sb-player/_mediator/sb-player.helper';
import { useSbPamZoomListener } from 'components/app/storyboard/sb-pan-zoom/sb-pan-zoom.provider';
import { setPanZoomControl, setSbPlayback, setSbWsPlayback } from './sb.slice';
import { useSbCb } from './use-sb-cb';
import { useSbContext } from './sb.provider';
import { useSbLogger } from './use-sb-logger';
import { SB_TRANSITION, SB_TRANSITION_PZ } from './sb-const';

export const useSbWs = (ws: WaveSurfer) => {
  const waveIsReady = useAppSelector((state) => state.audio.waveIsReady);
  const transitionEndListener = useAppSelector((state) => state.sb.transitionEndListener);
  const config = useAppSelector((state) => state.editor.storyConfig);
  const { isDragging } = useDraggerContext();
  const { getPlaybackReadyPlayers } = useSbPlayerCb();
  const { sbListenClip, sbListenSkipDamping, sbListenPanZoom } = useSbContext();
  const { playerRootElement, onInitParam, sbPlayerSeekTo } = useSbPlayerContext();
  const { handleTransitionEnd, handleTransition } = useSbPlayerTransition(playerRootElement);
  const { setSbPanZoomListener, sbPanZoomListenerRef } = useSbPamZoomListener();
  const { stopAnimation, runAnimation } = useAnimation(playerRootElement);
  const { handleFader, handleFaderToggle } = useSbFader();
  const { sbPlay, sbPause } = useSbCb();
  const { handleIdleTime } = useSbIdle();
  const getPlayback = useSbPlayerPlayback();
  const listenOnSeek = useRef(false);
  const dispatch = useAppDispatch();
  const logger = useSbLogger();
  const store = useAppStore();

  useEffect(() => {
    handleFaderToggle();
  }, [config?.startMarkerId, config?.finishMarkerId]);

  useEffect(() => {
    const setPlaybackData = (time: EditorTime, clipId: CellClip, cell: Index, track: StoryBoardTracks) => {
      const playback = getPlayback(cell, track);
      const dampingPlayerIndex = handleTransition(cell, time, track, playback);
      dispatch(setActiveClip(clipId));
      dispatch(setSbPosition({ cell, track }));
      dispatch(setPlayback({ ...playback, dampingPlayerIndex }));
    };
    const setPlaybackFile = (clipId: CellClip, selectedFile: FileId | null, clipsMap: ClipsMap | null) => {
      if (clipId === EMPTY_CELL || !clipsMap) return;
      const clipData = selectClipData(clipId, clipsMap);
      if (selectedFile === clipData.mediaFileId) return;
      dispatch(setSelectedFile(clipData.mediaFileId));
    };
    const seekPlayers = (wsTime: Seconds) => {
      const { playback, markers, sbClipPlaybackMap, sbCell } = selectData(store.getState());
      const { playbackClips, linkedGroupIndex } = playback;
      getPlaybackPlayers(playback).forEach((playerIndex, i) => {
        if (playerIndex === null || !playbackClips[playerIndex]) return;
        const isDamping = i === 1;
        const linkedGroup = isLinkedGroup(playbackClips[playerIndex]) ? (playbackClips[playerIndex] as ClipId[]) : null;
        const clipId =
          isDamping && linkedGroup
            ? linkedGroup[linkedGroup.length - 1]
            : linkedGroup
            ? linkedGroup[linkedGroupIndex!]
            : (playbackClips[playerIndex] as ClipId);
        const playbackParams = selectSbClipPlaybackParams(clipId, sbClipPlaybackMap);
        const time = getPlayerTime(wsTime, playbackParams, markers!, isDamping ? sbCell - 1 : sbCell);
        sbPlayerSeekTo(playerIndex, time);
      });
    };

    const onSeek = () => {
      const {
        sbCell,
        dragger,
        sbTrack,
        markers,
        duration,
        clipsMap,
        storyBoard,
        activeClip,
        selectedFile,
        sbWsPlayback,
        tracksRestored,
        panZoomKeyFrameIndex,
      } = selectData(store.getState());
      if (sbWsPlayback) return;
      if (!markers || !duration || !clipsMap) {
        console.error(`[${SRC}] storyboardSeekTo`);
        return;
      }
      if (ws.isPlaying()) {
        stopAnimation([SB_TRANSITION, SB_TRANSITION_PZ]);
        listenOnSeek.current = true;
        sbPause();
      }
      dispatch(setPanZoomControl(sbListenPanZoom.current));
      if (sbListenPanZoom.current) {
        sbListenPanZoom.current = false;
        if (panZoomKeyFrameIndex) return;
      }
      if (dragger === EnumDragger.cell) {
        onInitParam.current = onInitEnum.onCellResize;
        return;
      }
      onInitParam.current = sbListenClip.current ? onInitEnum.onSelect : onInitEnum.onSeek;

      const time = ws.getCurrentTime();
      const track = sbListenClip.current?.track ?? sbTrack;
      const cell =
        sbListenClip.current?.cell ??
        (isDragging.current && isDraggingCell(time, sbCell, markers, duration) ? sbCell : getCursorCell(time, markers));
      const clipId = storyBoard[track][cell];
      const isSameCell = sbCell === cell && activeClip === clipId && sbTrack === track;

      if (isSameCell && !isDragging.current && !sbListenClip.current && !sbListenSkipDamping.current)
        handleFaderToggle();
      if (!tracksRestored || !isSameCell) {
        setPlaybackData(time, clipId, cell, track);
        setPlaybackFile(clipId, selectedFile, clipsMap);
        if (!sbListenClip.current) handleFaderToggle();
      } else {
        if (sbListenSkipDamping.current) sbListenSkipDamping.current = false;
        else dispatch(setDampingPlayerIndex(handleTransition(cell, time, track)));
      }
      setSbPanZoomListener({ time, cell, clipId });
      handleIdleTime(clipId, time);
      seekPlayers(time);
      handleFader(time);
      sbListenClip.current = null;
    };
    const onPlay = () => {
      runAnimation([SB_TRANSITION, SB_TRANSITION_PZ]);
      handleFaderToggle();
    };
    const onFinish = () => {
      dispatch(setSbPlayback(false));
      dispatch(setSbWsPlayback(false));
    };
    const onPause = () => {
      if (listenOnSeek.current) {
        listenOnSeek.current = false;
        return;
      }
      stopAnimation([SB_TRANSITION, SB_TRANSITION_PZ]);
      const time = ws.getCurrentTime();
      const { sbTrack, sbCell, activeClip } = selectData(store.getState());
      setSbPanZoomListener({ time, cell: sbCell, clipId: activeClip });
      handleTransition(sbCell, time, sbTrack);
      handleIdleTime(activeClip, time);
      handleFader(time);
    };
    const onRegionIn = (region: Region) => {
      const {
        storyBoard,
        sbTrack: track,
        sbWsPlayback,
        sbCell,
        sbPlayback,
        clipsMap,
        storyConfig,
        selectedFile,
      } = selectData(store.getState());
      if (!sbPlayback || sbWsPlayback) return;
      const cell = isRegionIn(region, sbCell);
      if (!cell) return;
      if (storyConfig?.finishMarkerId === cell - 1) {
        dispatch(setSbPlayback(false));
        return;
      }
      const time = ws.getCurrentTime();
      const clipId = storyBoard[track][cell];
      sbPanZoomListenerRef.current = { time, cell, clipId };
      setPlaybackData(time, clipId, cell, track);
      setPlaybackFile(clipId, selectedFile, clipsMap);
      const players = getPlaybackReadyPlayers();
      sbPlay(players, 'onRegionIn');
      logger?.regionIn(players, clipId);
      handleFaderToggle();
    };

    ws.on('seek', onSeek);
    ws.on('region-in', onRegionIn);
    ws.on('finish', onFinish);
    ws.on('pause', onPause);
    ws.on('play', onPlay);

    return () => {
      ws.un('seek', onSeek);
      ws.un('region-in', onRegionIn);
      ws.un('finish', onFinish);
      ws.un('pause', onPause);
      ws.un('play', onPlay);
    };
  }, [waveIsReady]);

  /* onRegionOut */

  useEffect(() => {
    if (!transitionEndListener) return;
    const { playback, sbCell } = selectData(store.getState());
    const { isEmptyCell, activePlayerIndex } = playback;
    const dampingPlayerIndex = handleTransitionEnd(isEmptyCell, activePlayerIndex, sbCell);
    dispatch(setDampingPlayerIndex(dampingPlayerIndex));
    sbPlay(getPlaybackReadyPlayers(), 'onRegionOut');
  }, [transitionEndListener]);
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { tracksRestored } = state.memory;
  const { duration } = state.audio;
  const { dragger } = state.app;
  const { selectedFile } = state.projects;
  const { sbPosition } = state.editor__controls;
  const { storyBoard, markers, activeClip, clipsMap, storyConfig, panZoomKeyFrameIndex } = state.editor;
  const { sbPlayback, sbWsPlayback, sbClipPlaybackMap } = state.sb;
  const playback = state.sb__player;
  return {
    selectedFile,
    storyBoard,
    activeClip,
    clipsMap,
    duration,
    markers,
    dragger,
    playback,
    sbPlayback,
    storyConfig,
    sbWsPlayback,
    tracksRestored,
    sbClipPlaybackMap,
    panZoomKeyFrameIndex,
    sbCell: sbPosition.cell,
    sbTrack: sbPosition.track,
  };
};
